import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import MarketSelection from './MarketSelection';

const BulkCouponsManager = ({ areas: initialAreas = [], available_coupons: initialCoupons = [] }) => {
  const [areas, setAreas] = useState(initialAreas);
  const [selectedArea, setSelectedArea] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]); // Store unfiltered data
  const [availableCoupons, setAvailableCoupons] = useState(initialCoupons);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState(new Set());
  const [planTypeFilter, setPlanTypeFilter] = useState('');
  const [recentOnlyFilter, setRecentOnlyFilter] = useState(false);
  const [excludeTrialFilter, setExcludeTrialFilter] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [lastResults, setLastResults] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [criteriaAction, setCriteriaAction] = useState({ 
    type: '', 
    coupon: '', 
    planType: '',
    excludeTrial: true, // Default to excluding trial users for safety
    skipExistingCoupons: false
  });
  const [criteriaPreview, setCriteriaPreview] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ isProcessing: false, processed: 0, total: 0, currentBatch: 0, showCompleted: false });
  const [accumulatedResults, setAccumulatedResults] = useState([]);

  useEffect(() => {
    // Initialize with props data
    setAreas(initialAreas);
    setAvailableCoupons(initialCoupons);
  }, [initialAreas, initialCoupons]);

  const fetchSubscriptionsForArea = async (areaId, planType = '', recentOnly = false) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ area_id: areaId });
      if (planType) params.append('plan_type', planType);
      if (recentOnly) params.append('recent_only', 'true');
      // Don't send exclude_trial to backend - we'll filter client-side
      
      const response = await fetch(`/admin/bulk_coupons?${params}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });
      const data = await response.json();
      
      setSelectedArea(data.area);
      setAllSubscriptions(data.subscriptions); // Store all data
      
      // Apply client-side trial filter if needed
      const filteredSubs = excludeTrialFilter 
        ? data.subscriptions.filter(s => !s.is_trialing)
        : data.subscriptions;
      
      setSubscriptions(filteredSubs);
      setAvailableCoupons(data.available_coupons);
      setSelectedSubscriptions(new Set());
      setTotalCount(data.subscriptions[0]?.total_count || data.subscriptions.length);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAreaChange = (e) => {
    const areaId = e.target.value;
    if (areaId) {
      fetchSubscriptionsForArea(areaId, planTypeFilter, recentOnlyFilter);
    } else {
      setSelectedArea(null);
      setSubscriptions([]);
      setAllSubscriptions([]);
      setAvailableCoupons([]);
      setSelectedSubscriptions(new Set());
      setTotalCount(0);
    }
  };


  const handleSubscriptionToggle = (subscriptionId) => {
    const newSelected = new Set(selectedSubscriptions);
    if (newSelected.has(subscriptionId)) {
      newSelected.delete(subscriptionId);
    } else {
      newSelected.add(subscriptionId);
    }
    setSelectedSubscriptions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSubscriptions.size === subscriptions.length) {
      setSelectedSubscriptions(new Set());
    } else {
      setSelectedSubscriptions(new Set(subscriptions.map(s => s.id)));
    }
  };

  const confirmBulkAction = (actionType) => {
    if (selectedSubscriptions.size === 0) {
      alert('Please select at least one subscription');
      return;
    }

    if (actionType === 'apply' && !selectedCoupon) {
      alert('Please select a coupon to apply');
      return;
    }

    const actionText = actionType === 'apply' 
      ? `apply coupon "${availableCoupons.find(c => c.id === selectedCoupon)?.name || selectedCoupon}"`
      : 'remove coupons';

    // Check if any selected subscriptions are trialing
    const selectedSubs = subscriptions.filter(s => selectedSubscriptions.has(s.id));
    const trialCount = selectedSubs.filter(s => s.is_trialing).length;

    setPendingAction({ type: actionType, text: actionText, trialCount: trialCount });
    setShowConfirmModal(true);
  };

  const executeBulkAction = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const response = await fetch('/admin/bulk_coupons/bulk_apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          subscription_ids: Array.from(selectedSubscriptions),
          coupon_id: selectedCoupon,
          action_type: pendingAction.type
        })
      });

      const results = await response.json();
      setLastResults(results);
      setShowResults(true);
      
      // Refresh the subscriptions data to show updated coupon info
      if (selectedArea) {
        await fetchSubscriptionsForArea(selectedArea.id, planTypeFilter, recentOnlyFilter);
      }
    } catch (error) {
      console.error('Failed to execute bulk action:', error);
      alert('Failed to execute bulk action. Check console for details.');
    } finally {
      setIsLoading(false);
      setPendingAction(null);
    }
  };

  const previewBulkCriteria = async () => {
    if (!selectedArea || !criteriaAction.type) return;
    
    setIsLoading(true);
    try {
      const response = await fetch('/admin/bulk_coupons/preview_criteria', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          area_id: selectedArea.id,
          plan_type: criteriaAction.planType,
          exclude_trial: criteriaAction.excludeTrial,
          skip_existing_coupons: criteriaAction.skipExistingCoupons,
          action_type: criteriaAction.type
        })
      });
      
      const preview = await response.json();
      setCriteriaPreview(preview);
    } catch (error) {
      console.error('Failed to preview bulk criteria:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const executeBulkCriteriaAction = async () => {
    setShowCriteriaModal(false);
    setAccumulatedResults([]);
    setBatchProgress({ isProcessing: true, processed: 0, total: 0, currentBatch: 0 });
    
    let lastId = null;
    let hasMore = true;
    let batchNumber = 0;
    let allResults = [];
    let totalSuccessful = 0;
    let totalFailed = 0;
    let firstBatchTotal = 0;

    try {
      while (hasMore) {
        batchNumber++;
        
        const requestBody = {
          area_id: selectedArea.id,
          plan_type: criteriaAction.planType,
          coupon_id: criteriaAction.coupon,
          action_type: criteriaAction.type,
          exclude_trial: criteriaAction.excludeTrial,
          skip_existing_coupons: criteriaAction.skipExistingCoupons
        };
        
        if (lastId) {
          requestBody.last_id = lastId;
        }

        const response = await fetch('/admin/bulk_coupons/bulk_apply_by_criteria', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          },
          body: JSON.stringify(requestBody)
        });

        const batchResults = await response.json();
        
        // Accumulate results
        allResults = allResults.concat(batchResults.results);
        totalSuccessful += batchResults.summary.successful;
        totalFailed += batchResults.summary.failed;
        
        // Update progress
        if (batchNumber === 1) {
          // First batch gives us the total count
          firstBatchTotal = batchResults.summary.batch_processed + (batchResults.summary.remaining_count || 0);
          setBatchProgress(prev => ({
            ...prev,
            total: firstBatchTotal,
            processed: batchResults.summary.batch_processed,
            currentBatch: batchNumber
          }));
        } else {
          setBatchProgress(prev => ({
            ...prev,
            processed: prev.processed + batchResults.summary.batch_processed,
            currentBatch: batchNumber
          }));
        }
        
        // Update accumulated results for display
        setAccumulatedResults([...allResults]);
        
        // Check if there are more batches to process
        hasMore = batchResults.summary.has_more;
        lastId = batchResults.summary.last_processed_id;
        
        // Add a small delay between batches to avoid overwhelming the server
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Set final results
      setLastResults({
        results: allResults,
        summary: {
          total_processed: totalSuccessful + totalFailed,
          successful: totalSuccessful,
          failed: totalFailed,
          action: criteriaAction.type,
          coupon_id: criteriaAction.coupon,
          area_name: selectedArea.name,
          plan_type: criteriaAction.planType || 'all',
          showing_details_for: allResults.length
        }
      });
      setShowResults(true);
      
      // Mark as completed but keep modal open
      setBatchProgress(prev => ({
        ...prev,
        isProcessing: false,
        showCompleted: true
      }));
      
      // Refresh the current view
      if (selectedArea) {
        await fetchSubscriptionsForArea(selectedArea.id, planTypeFilter, recentOnlyFilter);
      }
    } catch (error) {
      console.error('Failed to execute bulk criteria action:', error);
      alert('Failed to execute bulk action. Check console for details.');
      setBatchProgress({ isProcessing: false, processed: 0, total: 0, currentBatch: 0, showCompleted: false });
    } finally {
      setCriteriaAction({ type: '', coupon: '', planType: '', excludeTrial: true, skipExistingCoupons: false });
      setCriteriaPreview(null);
    }
  };

  const formatCouponDisplay = (coupon) => {
    if (!coupon) return 'None';
    return `${coupon.name} - ${coupon.discount} (${coupon.duration})`;
  };

  const formatCouponOption = (coupon) => {
    let display = coupon.name;
    if (coupon.percent_off) {
      display += ` - ${coupon.percent_off}% off`;
    } else if (coupon.amount_off) {
      display += ` - $${coupon.amount_off/100} off`;
    }
    if (coupon.duration_in_months) {
      display += ` (${coupon.duration_in_months} months)`;
    } else {
      display += ` (${coupon.duration})`;
    }
    return display;
  };

  return (
    <div className="px-4 pt-2 pb-4 max-w-7xl mx-auto">
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-900">Bulk Coupon Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Select a market to view active subscriptions and manage coupons in bulk
        </p>
      </div>

      <MarketSelection 
        areas={areas}
        selectedArea={selectedArea}
        planTypeFilter={planTypeFilter}
        recentOnlyFilter={recentOnlyFilter}
        excludeTrialFilter={excludeTrialFilter}
        subscriptions={subscriptions}
        totalCount={totalCount}
        isLoading={isLoading}
        onAreaChange={handleAreaChange}
        onPlanTypeChange={(e) => {
          const newPlanType = e.target.value;
          setPlanTypeFilter(newPlanType);
          if (selectedArea) {
            fetchSubscriptionsForArea(selectedArea.id, newPlanType, recentOnlyFilter);
          }
        }}
        onRecentOnlyChange={(e) => {
          const newRecentOnly = e.target.checked;
          setRecentOnlyFilter(newRecentOnly);
          if (selectedArea) {
            fetchSubscriptionsForArea(selectedArea.id, planTypeFilter, newRecentOnly);
          }
        }}
        onExcludeTrialChange={(e) => {
          const newExcludeTrial = e.target.checked;
          setExcludeTrialFilter(newExcludeTrial);
          
          // Filter locally without API call
          if (allSubscriptions.length > 0) {
            const filteredSubs = newExcludeTrial 
              ? allSubscriptions.filter(s => !s.is_trialing)
              : allSubscriptions;
            setSubscriptions(filteredSubs);
            setSelectedSubscriptions(new Set()); // Clear selections when filtering
          }
        }}
      />

      {selectedArea && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Bulk Apply by Criteria (Fast)</CardTitle>
            <p className="text-sm text-gray-600">Apply coupons to hundreds/thousands of subscriptions without loading them individually</p>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action:
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={criteriaAction.type}
                    onChange={(e) => {
                      setCriteriaAction(prev => ({ ...prev, type: e.target.value }));
                      setCriteriaPreview(null);
                    }}
                    disabled={isLoading}
                  >
                    <option value="">Choose action...</option>
                    <option value="apply">Apply Coupon</option>
                    <option value="remove">Remove Coupons</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Type:
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={criteriaAction.planType}
                    onChange={(e) => {
                      setCriteriaAction(prev => ({ ...prev, planType: e.target.value }));
                      setCriteriaPreview(null);
                    }}
                    disabled={isLoading}
                  >
                    <option value="">All Plans</option>
                    <option value="annual">Annual Only</option>
                    <option value="monthly">Monthly Only</option>
                    <option value="quarterly">Quarterly Only</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon:
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={criteriaAction.coupon}
                    onChange={(e) => {
                      setCriteriaAction(prev => ({ ...prev, coupon: e.target.value }));
                      setCriteriaPreview(null);
                    }}
                    disabled={isLoading || criteriaAction.type !== 'apply'}
                  >
                    <option value="">Select coupon...</option>
                    {availableCoupons.map(coupon => (
                      <option key={coupon.id} value={coupon.id}>
                        {formatCouponOption(coupon)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="border-t pt-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={criteriaAction.excludeTrial}
                      onChange={(e) => {
                        setCriteriaAction(prev => ({ ...prev, excludeTrial: e.target.checked }));
                        setCriteriaPreview(null);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Exclude trial users
                    </span>
                    <span className="text-xs text-gray-500">(recommended - they likely have credits)</span>
                  </label>
                  
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={criteriaAction.skipExistingCoupons}
                      onChange={(e) => {
                        setCriteriaAction(prev => ({ ...prev, skipExistingCoupons: e.target.checked }));
                        setCriteriaPreview(null);
                      }}
                      disabled={criteriaAction.type !== 'apply'}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Skip users with existing coupons
                    </span>
                  </label>
                </div>
              </div>

              {criteriaPreview && (
                <div className="bg-white rounded-lg p-3 mb-4 border border-blue-200">
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900 mb-2">Preview:</div>
                    <div className="space-y-1">
                      <div>
                        <span className="text-gray-600">Will affect:</span>{' '}
                        <span className="font-bold text-green-600">{criteriaPreview.will_affect}</span> subscriptions
                      </div>
                      {criteriaPreview.excluded_trial > 0 && (
                        <div>
                          <span className="text-gray-600">Excluding:</span>{' '}
                          <span className="font-bold text-orange-600">{criteriaPreview.excluded_trial}</span> trial users
                        </div>
                      )}
                      {criteriaPreview.excluded_with_coupons > 0 && (
                        <div>
                          <span className="text-gray-600">Skipping:</span>{' '}
                          <span className="font-bold text-orange-600">{criteriaPreview.excluded_with_coupons}</span> users with existing coupons
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Total in selection: {criteriaPreview.total_count} subscriptions
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex space-x-3">
                <Button 
                  onClick={previewBulkCriteria}
                  disabled={isLoading || !criteriaAction.type || (criteriaAction.type === 'apply' && !criteriaAction.coupon)}
                  variant="outline"
                  className="flex-1"
                >
                  Preview Impact
                </Button>
                <Button 
                  onClick={() => setShowCriteriaModal(true)}
                  disabled={isLoading || !criteriaAction.type || (criteriaAction.type === 'apply' && !criteriaAction.coupon)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Execute Bulk Action
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedArea && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Manual Selection (Preview Only)</CardTitle>
            <p className="text-sm text-gray-600">Preview first 50 subscriptions - use bulk criteria above for large operations</p>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon to Apply:
                  </label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    value={selectedCoupon}
                    onChange={(e) => setSelectedCoupon(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">Select coupon...</option>
                    {availableCoupons.map(coupon => (
                      <option key={coupon.id} value={coupon.id}>
                        {formatCouponOption(coupon)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="lg:col-span-2 flex items-end">
                  <div className="space-x-3">
                    <Button 
                      onClick={() => confirmBulkAction('apply')}
                      disabled={isLoading || selectedSubscriptions.size === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Apply Coupon to Selected ({selectedSubscriptions.size})
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => confirmBulkAction('remove')}
                      disabled={isLoading || selectedSubscriptions.size === 0}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50"
                    >
                      Remove Coupons from Selected ({selectedSubscriptions.size})
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-600">Processing...</p>
              </div>
            )}

            {subscriptions.length > 0 && !isLoading && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isLoading}
                  >
                    {selectedSubscriptions.size === subscriptions.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <span className="text-sm text-gray-600">
                    {selectedSubscriptions.size} of {subscriptions.length} selected
                  </span>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Select</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trial End</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Renewal</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Coupon</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {subscriptions.map((subscription, index) => (
                        <tr key={subscription.id} className={subscription.is_trialing ? 'bg-yellow-50' : (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input 
                              type="checkbox"
                              checked={selectedSubscriptions.has(subscription.id)}
                              onChange={() => handleSubscriptionToggle(subscription.id)}
                              disabled={isLoading || subscription.stripe_error}
                              className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${subscription.stripe_error ? 'opacity-50 cursor-not-allowed' : ''}`}
                            />
                          </td>
                          <td className="px-4 py-4">
                            <a 
                              href={`/accounts/${subscription.account_id}/edit`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                {subscription.account_name || 'N/A'}
                              </div>
                            </a>
                            <div className="text-sm text-gray-500">
                              {subscription.account_email}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              {subscription.plan_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Badge 
                                variant={subscription.status === 'active' ? 'default' : 'secondary'}
                                className={subscription.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : subscription.is_trialing
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-yellow-100 text-yellow-800'
                                }
                              >
                                {subscription.status}
                              </Badge>
                              {subscription.is_trialing && (
                                <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                                  TRIAL
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            {subscription.is_trialing && subscription.trial_end ? (
                              <div>
                                <div className="text-gray-900">{subscription.trial_end}</div>
                                <div className="text-xs text-gray-500">⚠️ Has credits</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.created_at || 'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.renewal_date || 'N/A'}
                          </td>
                          <td className="px-4 py-4">
                            {subscription.stripe_error ? (
                              <div className="text-sm">
                                <Badge variant="destructive" className="bg-red-100 text-red-800">
                                  ⚠️ Stripe Sync Issue
                                </Badge>
                                <div className="text-xs text-gray-500 mt-1">Cannot apply coupons</div>
                              </div>
                            ) : subscription.current_coupon ? (
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">{subscription.current_coupon.name}</div>
                                <div className="text-gray-500">{subscription.current_coupon.discount} ({subscription.current_coupon.duration})</div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">None</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showResults && lastResults && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Bulk Action Results</CardTitle>
            <div className="text-sm text-gray-600">
              {lastResults.summary.action === 'apply' ? 'Applied' : 'Removed'} {lastResults.summary.coupon_id || 'coupons'} - 
              {' '}<span className="font-semibold text-green-600">{lastResults.summary.successful} successful</span>, 
              {' '}<span className="font-semibold text-red-600">{lastResults.summary.failed} failed</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Coupon</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lastResults.results.map((result, index) => (
                    <tr key={index} className={result.success ? 'bg-green-50' : 'bg-red-50'}>
                      <td className="px-4 py-4 text-sm text-gray-900">{result.account_email}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Badge 
                          variant={result.success ? 'default' : 'destructive'}
                          className={result.success 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                          }
                        >
                          {result.success ? 'Success' : 'Failed'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">{result.message}</td>
                      <td className="px-4 py-4">
                        {result.current_coupon ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{result.current_coupon.name}</div>
                            <div className="text-gray-500">{result.current_coupon.discount} ({result.current_coupon.duration})</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex space-x-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setShowResults(false);
                  setLastResults(null);
                }}
              >
                Clear Results
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowResults(false)}
              >
                Hide Results
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showConfirmModal && pendingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Confirm Bulk Action</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to {pendingAction.text} for {selectedSubscriptions.size} selected subscriptions?
              </p>
              {pendingAction.trialCount > 0 && pendingAction.type === 'apply' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800 font-semibold">
                    ⚠️ Warning: {pendingAction.trialCount} Trial User{pendingAction.trialCount > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    You've selected {pendingAction.trialCount} trial user{pendingAction.trialCount > 1 ? 's' : ''} who may already have Stripe account credits applied. 
                    Applying additional coupons might result in excessive discounts.
                  </p>
                </div>
              )}
              <div className="flex space-x-3 justify-end">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingAction(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={executeBulkAction}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Yes, Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showCriteriaModal && criteriaAction.type && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Confirm Bulk Criteria Action</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Large Scale Operation</h4>
                <p className="text-sm text-yellow-700">
                  This will {criteriaAction.type} {criteriaAction.type === 'apply' ? `coupon "${availableCoupons.find(c => c.id === criteriaAction.coupon)?.name}"` : 'coupons'} to subscriptions in {selectedArea.name}.
                </p>
              </div>
              
              {criteriaPreview ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <div className="text-sm space-y-1">
                    <div className="font-semibold text-blue-900">Impact Summary:</div>
                    <div>
                      ✓ Will process: <span className="font-bold text-green-600">{criteriaPreview.will_affect}</span> subscriptions
                    </div>
                    {criteriaAction.excludeTrial && criteriaPreview.excluded_trial > 0 && (
                      <div className="text-orange-700">
                        ✗ Excluding: {criteriaPreview.excluded_trial} trial users
                      </div>
                    )}
                    {criteriaAction.skipExistingCoupons && criteriaPreview.excluded_with_coupons > 0 && (
                      <div className="text-orange-700">
                        ✗ Skipping: ~{criteriaPreview.excluded_with_coupons} users with existing coupons
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-3 mb-4 text-sm text-gray-600">
                  Click "Preview Impact" first to see exactly how many subscriptions will be affected.
                </div>
              )}
              
              <div className="space-y-2 text-sm mb-4">
                <div><strong>Market:</strong> {selectedArea.name}</div>
                <div><strong>Plan Type:</strong> {criteriaAction.planType || 'All Plans'}</div>
                <div><strong>Action:</strong> {criteriaAction.type === 'apply' ? 'Apply Coupon' : 'Remove Coupons'}</div>
                {criteriaAction.type === 'apply' && (
                  <div><strong>Coupon:</strong> {availableCoupons.find(c => c.id === criteriaAction.coupon)?.name}</div>
                )}
                <div><strong>Exclude Trial Users:</strong> {criteriaAction.excludeTrial ? 'Yes' : 'No'}</div>
                {criteriaAction.type === 'apply' && (
                  <div><strong>Skip Existing Coupons:</strong> {criteriaAction.skipExistingCoupons ? 'Yes' : 'No'}</div>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                This operation will process subscriptions in batches of 50 and may take several minutes to complete.
              </p>
              
              <div className="flex space-x-3 justify-end">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowCriteriaModal(false);
                    setCriteriaAction({ type: '', coupon: '', planType: '', excludeTrial: true, skipExistingCoupons: false });
      setCriteriaPreview(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={executeBulkCriteriaAction}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Yes, Execute Bulk Action
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {(batchProgress.isProcessing || batchProgress.showCompleted) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>
                {batchProgress.showCompleted ? 'Bulk Action Complete' : 'Processing Bulk Action'}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {batchProgress.showCompleted 
                  ? 'All subscriptions have been processed successfully.' 
                  : 'Processing subscriptions in batches of 50. Please do not close this window.'}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {batchProgress.showCompleted ? 'Final Result:' : 'Progress:'} {batchProgress.processed} of {batchProgress.total || '...'} subscriptions
                    </span>
                    {!batchProgress.showCompleted && (
                      <span className="text-sm text-gray-500">
                        Batch {batchProgress.currentBatch}
                      </span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`${batchProgress.showCompleted ? 'bg-green-600' : 'bg-blue-600'} h-3 rounded-full transition-all duration-300`}
                      style={{ 
                        width: `${batchProgress.total ? (batchProgress.processed / batchProgress.total * 100) : 0}%` 
                      }}
                    />
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {batchProgress.total ? Math.round(batchProgress.processed / batchProgress.total * 100) : 0}% complete
                  </div>
                </div>

                {accumulatedResults.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      {batchProgress.showCompleted ? 'Final Results:' : 'Current Results:'}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-green-700">
                          {accumulatedResults.filter(r => r.success).length}
                        </div>
                        <div className="text-sm text-green-600">Successful</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-red-700">
                          {accumulatedResults.filter(r => !r.success).length}
                        </div>
                        <div className="text-sm text-red-600">Failed</div>
                      </div>
                    </div>
                  </div>
                )}

                {batchProgress.showCompleted ? (
                  <div className="flex justify-center pt-4">
                    <Button 
                      onClick={() => {
                        setBatchProgress({ isProcessing: false, processed: 0, total: 0, currentBatch: 0, showCompleted: false });
                        setAccumulatedResults([]);
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Close Results
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center pt-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-sm text-gray-600">Processing batch {batchProgress.currentBatch}...</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BulkCouponsManager;